'use client';

import { useState, useMemo, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/hooks/useCart';
import { Plus, Minus, ChevronDown } from 'lucide-react';
import { useUser } from '../../contexts/UserContext';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export const ProductCard = ({ product, onSelect, category, isCartBlocked = false }) => {
  const { cart, addToCart, removeFromCart, updateQuantity } = useCart();
  const { user } = useUser();
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const timeoutRef = useRef(null);
  const popoverRef = useRef(null);
  const observerRef = useRef(null);
  // const isSingleCategory = ['Grocery', 'Extras'].includes(category);
  const isSingleCategory = product?.productVariants
    ? product?.productVariants?.length < 1
      ? true
      : false
    : true;
  console.log(isSingleCategory);

  const variants = useMemo(() => product?.productVariants || [], [product?.productVariants]);
  const selectedVariant = variants[selectedVariantIndex];

  useEffect(() => {
    // Find if any variant is already in cart
    const cartVariant = variants.findIndex((variant) =>
      cart.some(
        (item) => item.id === product.productId && item.variant.inventoryId === variant.inventoryId
      )
    );
    // Update selected variant index if found in cart
    if (cartVariant >= 0) {
      setSelectedVariantIndex(cartVariant);
    }
  }, [variants, cart, product.productId]);

  const cartItems = useMemo(() => {
    return cart.filter((item) => item?.id === product?.productId);
  }, [cart, product.productId]);

  const customWeightItem = useMemo(() => {
    return cartItems?.find((item) => item?.variant?.isCustomWeight);
  }, [cartItems]);

  const standardCartItem = useMemo(() => {
    return cart.find(
      (item) =>
        item?.id === product?.productId &&
        item?.variant?.inventoryId === selectedVariant?.inventoryId
    );
  }, [cart, product?.productId, selectedVariant?.inventoryId]);

  const singleCartItem = useMemo(() => {
    return cart?.find((item) => item?.id === product?.productId);
  }, [cart, product?.productId, selectedVariant?.inventoryId]);

  const cartItem = useMemo(() => {
    if (customWeightItem && !standardCartItem) {
      return customWeightItem;
    } else if (!standardCartItem) {
      return singleCartItem;
    }
    return standardCartItem;
  }, [customWeightItem, standardCartItem, singleCartItem]);

  const quantity = cartItem ? cartItem?.quantity : 0;

  const discount = useMemo(() => {
    if (!selectedVariant) return 0;
    const mrp = selectedVariant.mrp || product.mrp;
    const netPrice = selectedVariant.netPrice || product.netPrice;
    return mrp && netPrice ? (((mrp - netPrice) / mrp) * 100).toFixed(0) : 0;
  }, [selectedVariant, product.mrp, product.netPrice]);

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (quantity === 0) {
      addToCart({
        id: product.productId,
        name: product.productName,
        image: product.productImageUrl,
        mrp: selectedVariant ? selectedVariant.mrp : product?.mrp,
        price: selectedVariant ? selectedVariant.netPrice : product.netPrice,
        quantity: 1,
        unit: product?.baseUnit,
        variant: selectedVariant ? selectedVariant : 1,
      });
    }
  };

  const handleIncrease = (e) => {
    e.stopPropagation();

    // Handle custom weight item - don't allow increasing quantity for custom weight
    if (cartItem?.variant?.isCustomWeight) {
      return;
    }

    if (quantity < 9) {
      if (['Grocery', 'Extras'].includes(category)) {
        updateQuantity(product.productId, '', 1);
      } else {
        updateQuantity(product.productId, selectedVariant.inventoryId, 1);
      }
    }
  };

  const handleDecrease = (e) => {
    e.stopPropagation();

    // Handle custom weight item
    if (cartItem?.variant?.isCustomWeight) {
      if (cartItem.quantity > 1) {
        updateQuantity(product.productId, cartItem.variant.inventoryId, -1);
      } else if (cartItem.quantity === 1) {
        removeFromCart(product.productId, cartItem.variant.inventoryId);
      }
      return;
    }

    // Handle standard variant
    if (quantity >= 1) {
      updateQuantity(product.productId, '', -1);
    } else if (quantity === 1) {
      removeFromCart(product.productId, selectedVariant.inventoryId);
    }
  };

  const handleVariantChange = (index) => {
    setSelectedVariantIndex(index);

    // Auto-add the selected variant to cart if not already there
    const newVariant = variants[index];
    const existingItem = cart.find(
      (item) =>
        item.id === product.productId && item.variant.inventoryId === newVariant?.inventoryId
    );

    if (!existingItem && product?.available && !isCartBlocked) {
      addToCart({
        id: product.productId,
        name: product.productName,
        image: product.productImageUrl,
        mrp: newVariant.mrp || product.mrp,
        price: newVariant.netPrice || product.netPrice,
        quantity: 1,
        variant: newVariant,
      });
    }

    // Close the popover
    setOpen(false);
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  // Update the handleMouseLeave function to also check visibility
  const handleMouseLeave = () => {
    setIsHovering(false);
    if (open) {
      setOpen(false);
    }
  };

  // Check if we have multiple variants
  const hasMultipleVariants = variants.length > 1;

  // Update selected variant when cart changes
  useEffect(() => {
    // If there's a custom weight item and no standard item, we want to show the custom weight
    if (customWeightItem && !standardCartItem) {
      return;
    }

    // Find the latest variant added to cart
    const latestVariantIndex = variants.findIndex((variant) =>
      cart.some(
        (item) => item.id === product.productId && item.variant.inventoryId === variant.inventoryId
      )
    );

    // Update selected variant if found and different from current
    if (latestVariantIndex >= 0 && latestVariantIndex !== selectedVariantIndex) {
      setSelectedVariantIndex(latestVariantIndex);
    }
  }, [cart, product.productId, variants, selectedVariantIndex, customWeightItem, standardCartItem]);

  // Determine what to display in the variant button
  const variantDisplay = useMemo(() => {
    if (cartItem?.variant?.isCustomWeight) {
      return `${cartItem.variant.weightInGrams}gm`;
    } else {
      return `${selectedVariant?.variant || 1} ${selectedVariant?.unit || 'pc'}`;
    }
  }, [cartItem, selectedVariant]);

  // Add a function to check if an element is in the viewport
  const isInViewport = (element) => {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };

  // Add an effect to set up the Intersection Observer
  useEffect(() => {
    if (open && popoverRef.current) {
      // Set up visibility check timer when popover opens
      const checkVisibilityInterval = setInterval(() => {
        if (!isHovering && !isInViewport(popoverRef.current)) {
          setOpen(false);
          clearInterval(checkVisibilityInterval);
        }
      }, 3000);

      // Clean up interval
      return () => {
        clearInterval(checkVisibilityInterval);
      };
    }
  }, [open, isHovering]);

  // Add cleanup for all timers and observers
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!product.available) {
    return null;
  }

  return (
    <div
      className={`bg-white rounded-lg overflow-hidden border relative ${
        product?.available ? 'cursor-pointer' : 'opacity-65 cursor-not-allowed'
      }`}
      onClick={() => {
        if (product?.available) {
          onSelect(product);
        }
      }}
    >
      {!product?.available && (
        <h2 className="absolute text-black font-bold z-50 top-1/2 text-center w-full -translate-y-1/2 text-balance text-base">
          Not Available
        </h2>
      )}
      <div className="relative">
        {Number(discount) > 0 && category !== 'Dairy' && (
          <div className="absolute top-2 left-2 bg-[#39c55e] text-white text-xs px-2 py-1 rounded">
            {discount}% OFF
          </div>
        )}
        <img
          loading="lazy"
          src={product.productImageUrl || '/placeholder.svg'}
          alt={product.productName}
          className="w-full h-32 object-cover"
          width={150}
          height={128}
        />
        {user?.userType === 'user' && (
          <div className="absolute -bottom-3 right-2">
            {quantity === 0 ? (
              <Button
                variant="secondary"
                size="sm"
                className="border border-[#39c55e]"
                onClick={handleAddToCart}
                disabled={!product?.available || isCartBlocked}
              >
                Add
              </Button>
            ) : (
              <div
                onClick={(e) => e.stopPropagation()}
                className="flex items-center bg-white border border-[#39c55e] rounded-md"
              >
                <Button variant="ghost" size="sm" className="p-0 h-8 w-8" onClick={handleDecrease}>
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="px-2 font-medium">{quantity}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-0 h-8 w-8"
                  onClick={handleIncrease}
                  disabled={quantity >= 9}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="p-3 space-y-2">
        <h3 className="font-medium text-sm">{product.productName}</h3>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <span className="text-sm font-bold">
              ₹
              {!isSingleCategory
                ? cartItem?.variant?.isCustomWeight
                  ? cartItem.price.toFixed(2)
                  : selectedVariant?.netPrice
                : product?.netPrice}
            </span>
            {!isSingleCategory &&
              selectedVariant?.mrp !== selectedVariant?.netPrice &&
              category !== 'Dairy' &&
              !cartItem?.variant?.isCustomWeight && (
                <span className="text-xs text-gray-500 line-through">₹{selectedVariant?.mrp}</span>
              )}
            {isSingleCategory && (
              <span className="text-xs ml-1 text-gray-500">
                MRP ₹<span className="line-through">{product?.mrp}</span>
              </span>
            )}
          </div>
          {/* Variant Selection */}
          {user?.userType === 'user' &&
          ((!isSingleCategory && hasMultipleVariants) || customWeightItem) ? (
            <Popover
              open={open}
              onOpenChange={() => {
                if (open) {
                  setOpen(false);
                } else {
                  setOpen(true);
                  setTimeout(() => {
                    setOpen(false);
                  }, 1500);
                }
              }}
            >
              <PopoverTrigger asChild>
                <button
                  className="text-sm border rounded-md px-2 py-1 flex items-center gap-1 hover:bg-gray-50"
                  onClick={(e) => e.stopPropagation()}
                >
                  {variantDisplay}
                  <ChevronDown className="h-3 w-3" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="w-36 mr-10 p-0"
                onClick={(e) => e.stopPropagation()}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                ref={popoverRef}
              >
                <div className="py-1">
                  {variants.map((variant, index) => {
                    // Find if this variant is in cart
                    const variantInCart = cart.find(
                      (item) =>
                        item.id === product.productId &&
                        item.variant.inventoryId === variant.inventoryId
                    );

                    return (
                      <button
                        key={variant.inventoryId}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                          index === selectedVariantIndex && !customWeightItem
                            ? 'bg-gray-50 font-medium'
                            : ''
                        } ${variantInCart ? 'flex justify-between items-center' : ''}`}
                        onClick={() => handleVariantChange(index)}
                      >
                        <span>
                          {variant.variant} {variant.unit} - ₹{variant.netPrice}
                        </span>
                        {variantInCart && (
                          <span className="text-xs bg-gray-200 px-1 rounded">
                            x{variantInCart.quantity}
                          </span>
                        )}
                      </button>
                    );
                  })}
                  {/* Custom weight item is removed from dropdown */}
                  <button
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100`}
                    onClick={() => {
                      onSelect(product);
                      setOpen(false);
                    }}
                  >
                    <span>Custom</span>
                  </button>
                </div>
              </PopoverContent>
            </Popover>
          ) : (
            <>
              {' '}
              {!isSingleCategory ? (
                <div className="text-sm border rounded-md px-2 py-1">{variantDisplay}</div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

ProductCard.displayName = 'ProductCard';
